import { useState } from 'react';

/** Why the learner says the suggested opening is wrong (FR-017d). */
export type AnswerReason = 'pass' | 'other';

export interface AnswerFlow {
  /** The Yes/No choice, or null before one is made. */
  chosen: 'yes' | 'no' | null;
  /** After "No": the reason picked, or null while the WhyNotPad is still open. */
  reason: AnswerReason | null;
  /** The bid level the call pad is showing suits for, or null. */
  selectedLevel: number | null;
  /**
   * The call the learner submitted, kept because the pad clears itself on submit — the verdict
   * echoes it back beside the right answer, so it has to outlive the pad.
   */
  submittedCall: string | null;
  setSelectedLevel: (level: number | null) => void;
  /** Yes submits at once; No waits for the reason before it is graded. */
  answerYesNo: (answer: 'yes' | 'no') => Promise<void>;
  /** "Insufficient points" grades immediately; "something else" opens the bid step instead. */
  chooseReason: (why: AnswerReason) => Promise<void>;
  /** The bid the learner thinks should have been opened instead. */
  submitOtherBid: (bid: string) => Promise<void>;
  submitCall: (call: string) => Promise<void>;
  backToYesNo: () => void;
  backToReason: () => void;
  /** Clears the whole chain — for moving to another hand, however that happens. */
  reset: () => void;
}

/**
 * The call named inside a submitted token, or null when the answer named none.
 *
 * The inverse of the tokens built below, and it lives beside them for that reason: a second
 * reading of `no:other:<bid>` somewhere else is how the two would drift. Needed because the flow's
 * own `submittedCall` only ever holds the LIVE question's answer — a session that lets the learner
 * step back to an earlier verdict has to recover the call from what it recorded at the time.
 */
export function callFromAnswerToken(token: string | null | undefined): string | null {
  if (!token) return null;
  // "Yes" and "insufficient points" name a judgement, not a call; the pad that made them is
  // still on screen either way.
  if (token === 'yes' || token === 'no:pass') return null;
  if (token.startsWith('no:other:')) return token.slice('no:other:'.length) || null;
  // A call module's token IS the call.
  return token;
}

/**
 * The answer-entry state machine shared by every table session (feature 023, FR-017d).
 *
 * Two shapes in one: a call pad answers in a single step, while a Yes/No drill can run
 * Yes/No → "Why not?" → "Which bid instead?", with each step undoable because nothing is graded
 * until it is submitted. The wire tokens it produces — `yes`, `no:pass`, `no:other:<bid>` — are
 * the server's contract and are not the caller's to choose, which is why they are built here
 * rather than at each call site.
 *
 * Feature-neutral: it holds only what the learner has entered so far, and knows nothing about
 * pools, rounds, streaks or what happens after a verdict.
 *
 * @param submit Records an answer. Whatever it resolves to is the caller's business — this hook
 *               only awaits it so the pads stay frozen while a grade is in flight.
 */
export function useAnswerFlow(submit: (answer: string) => Promise<void>): AnswerFlow {
  const [chosen, setChosen] = useState<'yes' | 'no' | null>(null);
  // After "No", the learner picks WHY before the answer is graded (FR-017d). Null until they do.
  const [reason, setReason] = useState<AnswerReason | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [submittedCall, setSubmittedCall] = useState<string | null>(null);

  return {
    chosen,
    reason,
    selectedLevel,
    submittedCall,
    setSelectedLevel,

    answerYesNo: async (answer) => {
      setChosen(answer);
      if (answer === 'no') return;
      setReason(null);
      await submit('yes');
    },

    chooseReason: async (why) => {
      setReason(why);
      if (why === 'pass') await submit('no:pass');
    },

    // Correct only if it matches the call actually made.
    submitOtherBid: async (bid) => {
      setSubmittedCall(bid);
      setSelectedLevel(null);
      await submit(`no:other:${bid}`);
    },

    submitCall: async (call) => {
      setSubmittedCall(call);
      setSelectedLevel(null);
      await submit(call);
    },

    // Step-back handlers (client request, 2026-07-28): each follow-up step can be undone. Nothing
    // has been graded on either step, so backing out is pure UI state — nothing to tell the server.
    backToYesNo: () => {
      setChosen(null);
      setReason(null);
    },
    backToReason: () => {
      setReason(null);
      // A half-picked bid level dies with the pad (CallPad remounts clean), so this copy of it
      // must go too.
      setSelectedLevel(null);
    },

    reset: () => {
      setChosen(null);
      setReason(null);
      setSubmittedCall(null);
      setSelectedLevel(null);
    },
  };
}
